import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CartItem, Language, Product, Theme } from "./types";
import { normalizeProduct } from "./catalog";

interface StoreContextValue {
  lang: Language;
  theme: Theme;
  products: Product[];
  loading: boolean;
  cart: CartItem[];
  wishlist: string[];
  cartCount: number;
  setLang: (lang: Language) => void;
  toggleTheme: () => void;
  addToCart: (product: Product, quantity?: number) => void;
  updateQuantity: (id: string, quantity: number) => void;
  removeFromCart: (id: string) => void;
  toggleWishlist: (id: string) => void;
  clearCart: () => void;
}

const StoreContext = createContext<StoreContextValue | null>(null);

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    return JSON.parse(localStorage.getItem(key) || "") as T;
  } catch {
    return fallback;
  }
};

export function StoreProvider({ children }: { children: ReactNode }) {
  const [lang, setLanguage] = useState<Language>(
    () => (localStorage.getItem("marka-lang") as Language) || "ar",
  );
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem("marka-theme") as Theme) || "light",
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState<CartItem[]>(() =>
    readJson("marka-cart", []),
  );
  const [wishlist, setWishlist] = useState<string[]>(() =>
    readJson("marka-wishlist", []),
  );

  useEffect(() => {
    fetch("/data/products.json")
      .then((r) => r.json())
      .then((items: Product[]) => setProducts(items.map(normalizeProduct)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem("marka-lang", lang);
  }, [lang]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("marka-theme", theme);
  }, [theme]);

  useEffect(
    () => localStorage.setItem("marka-cart", JSON.stringify(cart)),
    [cart],
  );
  useEffect(
    () => localStorage.setItem("marka-wishlist", JSON.stringify(wishlist)),
    [wishlist],
  );

  const addToCart = (product: Product, quantity = 1) =>
    setCart((items) => {
      const found = items.find((item) => item.product.id === product.id);
      return found
        ? items.map((item) =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + quantity }
              : item,
          )
        : [...items, { product, quantity }];
    });
  const updateQuantity = (id: string, quantity: number) =>
    setCart((items) =>
      items.map((item) =>
        item.product.id === id
          ? { ...item, quantity: Math.max(1, quantity) }
          : item,
      ),
    );
  const removeFromCart = (id: string) =>
    setCart((items) => items.filter((item) => item.product.id !== id));
  const toggleWishlist = (id: string) =>
    setWishlist((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );

  const value = useMemo(
    () => ({
      lang,
      theme,
      products,
      loading,
      cart,
      wishlist,
      cartCount: cart.reduce((sum, item) => sum + item.quantity, 0),
      setLang: setLanguage,
      toggleTheme: () =>
        setTheme((value) => (value === "light" ? "dark" : "light")),
      addToCart,
      updateQuantity,
      removeFromCart,
      toggleWishlist,
      clearCart: () => setCart([]),
    }),
    [lang, theme, products, loading, cart, wishlist],
  );

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
}

export const useStore = () => {
  const value = useContext(StoreContext);
  if (!value) throw new Error("useStore must be used inside StoreProvider");
  return value;
};

export const copy = {
  ar: {
    home: "الرئيسية",
    shop: "المنتجات",
    categories: "الأقسام",
    supplements: "المكملات",
    beauty: "العناية والجمال",
    mother: "الأم والطفل",
    medical: "الأجهزة والمستلزمات الصحية",
    offers: "العروض",
    about: "من نحن",
    contact: "تواصل معنا",
    search: "ابحث عن منتج، فيتامين، مكمل أو قسم...",
    add: "أضف إلى طلبك",
    inquire: "اسأل عن السعر",
    wishlist: "المفضلة",
    cart: "سلة الاستفسار",
    account: "حسابي",
    menu: "القائمة",
    viewAll: "عرض الكل",
    available: "متوفر",
    unavailable: "غير متوفر",
    confirmAvailability: "تأكيد التوفر",
    askPrice: "اسأل عن السعر",
    loadMore: "عرض المزيد",
    noResults: "لا توجد نتائج مطابقة",
    continue: "متابعة التسوق",
  },
  en: {
    home: "Home",
    shop: "Products",
    categories: "Categories",
    supplements: "Supplements",
    beauty: "Beauty & Care",
    mother: "Mother & Baby",
    medical: "Health Devices & Supplies",
    offers: "Offers",
    about: "About",
    contact: "Contact",
    search: "Search products, vitamins, supplements or categories...",
    add: "Add to Inquiry",
    inquire: "Ask for Price",
    wishlist: "Wishlist",
    cart: "Inquiry Cart",
    account: "Account",
    menu: "Menu",
    viewAll: "View all",
    available: "Available",
    unavailable: "Out of stock",
    confirmAvailability: "Confirm availability",
    askPrice: "Ask for Price",
    loadMore: "Load more",
    noResults: "No matching results",
    continue: "Continue shopping",
  },
} as const;
